import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import dxDataGrid from 'devextreme/ui/data_grid';
import {
  DxDataGridModule,
  DxButtonModule,
  DxCheckBoxModule
} from "devextreme-angular";
import {
  DxoEditingModule,
  DxiColumnModule,
  DxoLookupModule,
  DxiValidationRuleModule,
  DxiButtonModule as DxiButtonModuleNested
} from 'devextreme-angular/ui/nested';
import { CommonModule } from '@angular/common';
import { UtilitiesService } from '../framework/services/utilities.service';


@Component({
  selector: 'app-loan-amounts-grid',
  templateUrl: './loan-amounts.component.html',
  styleUrls: ['./loan-amounts.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DxDataGridModule,
    DxButtonModule,
    DxCheckBoxModule,
    DxoEditingModule,
    DxiColumnModule,
    DxoLookupModule,
    DxiValidationRuleModule,
    DxiButtonModuleNested
  ]
})
export class LoanAmountsComponent implements OnInit, OnDestroy {
  @Input() loanAmounts: any[] = [];
  @Input() loanData: any[] = [];
  @Input() showInactiveLoans: boolean = false;
  @Input() showAmountColumn: boolean = true;
  @Input() shouldDisableCheckbox: boolean = false;
  @Input() activeLoanCheckboxText: string = 'Show Inactive Loans';
  @Input() isGridInEditMode: boolean = false;
  @Input() loanItemTemplate: any;

  @Output() showInactiveLoansChange = new EventEmitter<boolean>();
  @Output() gridEditModeChange = new EventEmitter<boolean>();
  @Output() loanAmountsChange = new EventEmitter<any[]>();
  @Output() gridReady = new EventEmitter<dxDataGrid>();

  private grid: dxDataGrid | null = null;

  ngOnInit(): void {
    // Initialize loan amounts if not provided
    if (!this.loanAmounts) {
      this.loanAmounts = [];
      this.loanAmountsChange.emit(this.loanAmounts);
    }
  }

  ngOnDestroy(): void {
    if (this.grid) {
      this.grid.dispose();
    }
  }

  onCheckboxChange(e: any): void {
    if (this.shouldDisableCheckbox) {
      return;
    }

    const checked = !!e.value;
    this.showInactiveLoans = checked;
    this.showInactiveLoansChange.emit(checked);
  }

  onGridInitialized(grid: dxDataGrid): void {
    this.grid = grid;
    this.gridReady.emit(grid);
  }

  refreshGridLookup(): void {
    if (this.grid) {
      try {
        const loanColumn = this.grid.columnOption('loanNumber');
        if (loanColumn && loanColumn.lookup) {
          this.grid.columnOption('loanNumber', 'lookup.dataSource', this.getLoanData());
          this.grid.repaint();
        }
      } catch (error) {
        // console.log('Could not refresh grid lookup:', error);
      }
    }
  }

  getLoanStatusColor(loanNumber: string): string {
    const loan = this.loanData.find(l => l.name === loanNumber);
    return loan && loan.status === 'Inactive' ? 'red' : 'inherit';
  }

  getLoanDisplayText(loanNumber: string): string {
    const loan = this.loanData.find(l => l.name === loanNumber || l.loanNumber === loanNumber);
    if (!loan) return loanNumber || '';
    if (loan.dealName || loan.dealNumber || loan.facilityId || loan.facilityNumber) {
      return UtilitiesService.formatDealDisplay(loan);
    }
    return loan.status ? `${loan.name || loanNumber} (${loan.status})` : loan.name || loanNumber || '';
  }

  public getLoanData(): any[] {
    const baseLoanData = this.showInactiveLoans
      ? this.loanData
      : this.loanData.filter((l) => l.status !== 'Inactive');

    if (this.loanAmounts && this.loanAmounts.length > 0) {
      const firstLoan = this.loanData.find((ld) => ld.name === this.loanAmounts[0].loanNumber);
      const existingLoanNumbers = this.loanAmounts.map(item => item.loanNumber);

      if (firstLoan?.dealName) {
        const filteredLoans = baseLoanData.filter((l) => l.dealName === firstLoan.dealName);
        return filteredLoans.filter((l) => !existingLoanNumbers.includes(l.name));
      }

      return baseLoanData.filter((l) => !existingLoanNumbers.includes(l.name));
    }

    return baseLoanData;
  }

  private generateUniqueId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  // Custom add row method
  addNewRow(): void {
    if (this.grid && !this.isGridInEditMode) {
      this.grid.addRow();
    }
  }

  // Grid event handlers
  onRowInserted = (e: any) => {
    // console.log('Row inserted event fired:', e.data);
    if (!e.data.key) {
      e.data.key = this.generateUniqueId();
    }
    this.isGridInEditMode = false;
    this.gridEditModeChange.emit(false);
    this.loanAmountsChange.emit(this.loanAmounts);
    this.refreshGridLookup();
  };

  onRowUpdated = (e: any) => {
    // console.log('Row updated event fired:', e.data);
    this.isGridInEditMode = false;
    this.gridEditModeChange.emit(false);
    this.loanAmountsChange.emit(this.loanAmounts);
  };

  onRowRemoved = (e: any) => {
    // console.log('Row removed event fired:', e.data);
    this.loanAmountsChange.emit(this.loanAmounts);
    this.refreshGridLookup();
  };

  onEditingStart = (e: any) => {
    this.isGridInEditMode = true;
    this.gridEditModeChange.emit(true);
  };

  onEditCanceled = (e: any) => {
    this.isGridInEditMode = false;
    this.gridEditModeChange.emit(false);
  };

  onContentReady = (e: any) => {
    // console.log('Grid content ready, current data:', this.loanAmounts);
  };
}